'use client';

import { c as _c } from "react/compiler-runtime";
import type { Time } from '@morpho-org/morpho-ts';
import type { PropsChainIdAddress } from '@morpho-org/ui-v6/types';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { Select } from '@morpho-org/ui-v6/client';
import { Card, Skeleton } from '@morpho-org/ui-v6/server';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { memo, useState } from 'react';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config/table';
import { splitValueAndUnit } from '@/helpers/number';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { supplyHistoryQueryOptions, toHistoryRange, totalDepositsQueryOptions } from './totalDeposits.query';
import { toTotalDepositSeries } from './toTotalDepositSeries';
export type ChartTotalDepositClientProps = PropsChainIdAddress;
const USD = 'USD';

/**
 * The vault's total deposits now and over the picked timeframe, in USD or in the asset. The server
 * hydrates the current total and the default timeframe; another timeframe keeps the previous one on
 * screen in the chart's loading state until it arrives, and a failed one shows the chart's error
 * with the selectors still usable, as does a USD history whose points all lack a USD value. Any
 * other series with no value at all shows the no-data state. Without
 * the asset's decimals only USD is offered. A `partial` history still charts the points it has,
 * and marks the card with `data-partial`.
 */
const ChartTotalDepositClient = t0 => {
  const $ = _c(67);
  const {
    chainId,
    address
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  const [selectedCurrency, setCurrency] = useState(USD);
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t1 = totalDepositsQueryOptions({
      chainId,
      address
    });
    $[0] = address;
    $[1] = chainId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: latest
  } = useSuspenseQuery(t1);
  const {
    asset
  } = latest;
  const assetDecimals = asset.decimals;
  const symbol = asset.symbol ?? "Asset";
  let t2;
  if ($[3] !== assetDecimals || $[4] !== symbol) {
    t2 = assetDecimals === null ? [] : [{
      value: symbol,
      label: symbol
    }];
    $[3] = assetDecimals;
    $[4] = symbol;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      value: USD,
      label: USD
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2) {
    t4 = [...t2, t3];
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const currencyOptions = t4;
  const currency = assetDecimals === null ? USD : selectedCurrency;
  const isUsd = currency === USD;
  let t5;
  if ($[9] !== address || $[10] !== chainId || $[11] !== timeFrame) {
    t5 = supplyHistoryQueryOptions({
      chainId,
      address,
      range: toHistoryRange(timeFrame)
    });
    $[9] = address;
    $[10] = chainId;
    $[11] = timeFrame;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = {
      ...t5,
      placeholderData: keepPreviousData
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    data,
    isError,
    isPlaceholderData,
    isPending
  } = useQuery(t6);
  const loading = isPending || isPlaceholderData && !isError;
  let t7;
  if ($[15] !== assetDecimals || $[16] !== data?.points || $[17] !== isUsd) {
    t7 = toTotalDepositSeries(data?.points ?? [], isUsd ? "usd" : {
      decimals: assetDecimals
    });
    $[15] = assetDecimals;
    $[16] = data?.points;
    $[17] = isUsd;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    data: formattedData,
    missingUsd
  } = t7;
  let formattedValue;
  let t8;
  let totalAssetsUsd;
  if ($[19] !== asset.priceUsd || $[20] !== asset.symbol || $[21] !== assetDecimals || $[22] !== isUsd || $[23] !== latest.totalAssets || $[24] !== latest.totalAssetsUsd) {
    totalAssetsUsd = latest.totalAssetsUsd === null ? undefined : safeParseNumber(latest.totalAssetsUsd);
    const totalAssets = latest.totalAssets === null ? undefined : BigInt(latest.totalAssets);
    const decimals = assetDecimals ?? undefined;
    formattedValue = splitValueAndUnit(isUsd ? formatters.short.default("-").digits(2).of(totalAssetsUsd, 18) : formatters.short.default("-").digits(2).of(totalAssets, decimals));
    t8 = isUsd ? formatters.commas.unit("$").digits(2).of(totalAssetsUsd, 18) : formatters.commas.unit(asset.symbol ?? undefined).digits(getDigitsFromPriceAndAmount(asset.priceUsd === null ? undefined : safeParseNumber(asset.priceUsd), totalAssets, decimals)).of(totalAssets, decimals);
    $[19] = asset.priceUsd;
    $[20] = asset.symbol;
    $[21] = assetDecimals;
    $[22] = isUsd;
    $[23] = latest.totalAssets;
    $[24] = latest.totalAssetsUsd;
    $[25] = formattedValue;
    $[26] = t8;
    $[27] = totalAssetsUsd;
  } else {
    formattedValue = $[25];
    t8 = $[26];
    totalAssetsUsd = $[27];
  }
  const detailedFormattedValue = t8;
  const t9 = data?.partial ? "true" : undefined;
  let t10;
  if ($[28] !== currency) {
    t10 = <div className="flex items-center gap-[2px]"><span className="text-secondary-foreground text-xs">Total Deposits ({currency})</span></div>;
    $[28] = currency;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== detailedFormattedValue || $[31] !== formattedValue.unit || $[32] !== formattedValue.value || $[33] !== isUsd || $[34] !== totalAssetsUsd) {
    t11 = formattedValue.value ? <Tooltip><TooltipTrigger asChild={true}><div className="flex w-[fit-content] cursor-default items-center gap-[2px]" data-testid="total-deposits-value"><span className="text-title-sm">{isUsd && totalAssetsUsd !== undefined ? "$" : ""}{formattedValue.value}</span><span className="text-title-sm text-muted-foreground">{formattedValue.unit}</span></div></TooltipTrigger>{!!detailedFormattedValue && <TooltipContent side="right">{detailedFormattedValue}</TooltipContent>}</Tooltip> : <Skeleton className="h-(--text-title-sm--line-height) w-[130px] rounded-[20px]" />;
    $[30] = detailedFormattedValue;
    $[31] = formattedValue.unit;
    $[32] = formattedValue.value;
    $[33] = isUsd;
    $[34] = totalAssetsUsd;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <div className="gap-xs flex flex-col">{t10}{t11}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== currency || $[40] !== currencyOptions || $[41] !== isMobile) {
    t13 = isMobile ? <Select options={currencyOptions} value={currency} onChange={setCurrency} /> : <Tabs value={currency} onValueChange={setCurrency}><SegmentedControlList size="default" aria-label="Currency">{currencyOptions.map(_temp)}</SegmentedControlList></Tabs>;
    $[39] = currency;
    $[40] = currencyOptions;
    $[41] = isMobile;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== timeFrame) {
    t14 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} />;
    $[43] = timeFrame;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <div className="gap-sm flex flex-wrap items-center">{t13}{t14}</div>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t12 || $[49] !== t15) {
    t16 = <div className="gap-sm flex flex-wrap items-center justify-between">{t12}{t15}</div>;
    $[48] = t12;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const t17 = isError || missingUsd;
  let t18;
  if ($[51] !== loading || $[52] !== t17) {
    t18 = {
      loading,
      error: t17
    };
    $[51] = loading;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const t19 = isUsd ? "$" : currency;
  let t20;
  if ($[54] !== formattedData || $[55] !== palette.areaChartColors[0] || $[56] !== palette.colors[0]) {
    t20 = [{
      type: "areaspline",
      name: "Total Deposits",
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[54] = formattedData;
    $[55] = palette.areaChartColors[0];
    $[56] = palette.colors[0];
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t18 || $[60] !== t19 || $[61] !== t20) {
    t22 = <SimpleChart statuses={t18} unit={t19} height="224px" series={t20} additionalOptions={t21} />;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t16 || $[64] !== t22 || $[65] !== t9) {
    t23 = <Card className="gap-lg py-lg px-md md:px-lg w-[100%] flex-col" data-testid="total-deposits-chart-card" data-partial={t9} withBorder={true}>{t16}{t22}</Card>;
    $[63] = t16;
    $[64] = t22;
    $[65] = t9;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
};
export default memo(ChartTotalDepositClient);
function _temp(option) {
  return <SegmentedControlItem key={option.value} value={option.value}>{option.label}</SegmentedControlItem>;
}